import React from "react";
import PropTypes from "prop-types";
import {
  select,
  selectAll,
  scaleBand,
  scaleLinear,
  axisBottom,
  axisLeft,
} from "d3";

class GnomadHistogram extends React.Component {
  constructor() {
    super();
    this.state = { width: 300 };
  }

  componentDidMount() {
    this.setState({ width: Math.max(this.container.offsetWidth, 490) });
  }

  componentDidUpdate() {
    this.drawHistogram();
  }

  drawHistogram() {
    const { data, total, uniqueKey } = this.props;
    if (data) {
      const bar = uniqueKey.includes("het")
        ? "varsome-het-bar"
        : "varsome-hom-bar";

      selectAll(`#gnomad_ages_${uniqueKey} > svg > *`).remove();

      // Creates the initial space for the chart
      const svg = select(`#gnomad_ages_${uniqueKey} > svg`);
      const margin = { top: 20, right: 20, bottom: 80, left: 80 };
      const width = +svg.attr("width") - margin.left - margin.right;
      const height = +svg.attr("height") - margin.top - margin.bottom;

      const x = scaleBand().rangeRound([0, width]).padding(0.1);
      const y = scaleLinear().rangeRound([height, 0]);

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // get max value for y axis
      let maxy = 0;
      for (let i = data.length - 1; i >= 0; i--) {
        const val = parseFloat(data[i].value);
        if (val > maxy) {
          maxy = val;
        }
      }

      x.domain(
        data.map((d) => {
          return d.age;
        })
      );
      y.domain([0, maxy]).nice();

      const ticks = maxy > 10 ? 10 : maxy;

      g.append("g")
        .attr("class", "axis axis--x")
        .attr("transform", `translate(0,${height})`)
        .call(axisBottom(x))
        .selectAll("text")
        .attr("class", "varsome-axis-label")
        .style("text-anchor", "end")
        .attr("dx", "-.8em")
        .attr("dy", ".15em")
        .attr("transform", "rotate(-65)");

      g.append("g")
        .attr("class", "varsome-axis axis--y")
        .call(
          axisLeft(y)
            .ticks(ticks)
            .tickFormat((d) => {
              const array = ["", "k", "M", "G", "T", "P"];
              let i = 0;
              while (d > 1000) {
                i += 1;
                // eslint-disable-next-line no-param-reassign
                d /= 1000;
              }
              return `${d} ${array[i]}`;
            })
        )
        .append("text")
        .attr("class", "varsome-axis-label")
        .attr("y", 6)
        .attr("dy", "0.71em")
        .attr("text-anchor", "end")
        .text("value");

      g.append("text")
        .attr("text-anchor", "middle") // this makes it easy to centre the text as the transform is applied to the anchor
        .attr("transform", `translate(-40,${height / 2})rotate(-90)`) // text is drawn off the screen top left, move down and out and rotate
        .text("Individuals");

      g.append("text")
        .attr("text-anchor", "middle") // this makes it easy to centre the text as the transform is applied to the anchor
        .attr("transform", `translate(${width / 2},${height + 70})`) // centre below axis
        .text("Age");

      // For each value in our dataset,
      // places and styles a bar on the chart.
      const rect = g.selectAll("rect").data(data).enter().append("g");

      rect
        .append("rect")
        .attr("class", bar)
        .attr("x", (d) => {
          return x(d.age);
        })
        .attr("y", (d) => {
          return y(d.value);
        })
        .attr("width", x.bandwidth())
        .attr("height", (d) => {
          const barHeight = d.value === 0 ? 0 : height - y(d.value);
          return barHeight;
        });

      rect
        .append("text")
        .attr("text-anchor", "middle")
        .attr("y", (d) => {
          return y(d.value) - 5;
        })
        .attr("x", (d) => {
          return x(d.age) + x.bandwidth() / 2;
        })
        .style("fill", "black")
        .attr("font-size", "9px")
        .text((d) => {
          if (d.value !== 0) return d.value;
          return null;
        });

      rect.append("title").text((d) => {
        // eslint-disable-next-line no-restricted-globals
        const percentageValue = isNaN(d.value / total)
          ? "0%"
          : `${((100 * d.value) / total).toFixed(2)}%`;
        return percentageValue;
      });
    }
  }

  render() {
    const { uniqueKey, data } = this.props;
    const { width } = this.state;
    return (
      <div
        className="tw-w-full tw-overflow-x-auto"
        ref={(ref) => {
          this.container = ref;
        }}
        id={`gnomad_ages_${uniqueKey}`}
      >
        {data ? <svg width={width} height="350" /> : null}
      </div>
    );
  }
}

GnomadHistogram.propTypes = {
  uniqueKey: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      age: PropTypes.string,
      value: PropTypes.number,
    })
  ),
  total: PropTypes.number,
};

GnomadHistogram.defaultProps = {
  uniqueKey: null,
  data: null,
  total: 0,
};

export default GnomadHistogram;
